import React from 'react';

const ShareButtons = ({ variant = 'default' }) => {
  const [copied, setCopied] = React.useState(false);
  const [shareUrl, setShareUrl] = React.useState('');
  const [shareTitle, setShareTitle] = React.useState('');

  React.useEffect(() => {
    if (typeof window === 'undefined') return;
    setShareUrl(window.location.href);
    setShareTitle(document.title || 'LMD - Le Vrai Journal du Digital');
  }, []);

  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(shareTitle);

  const links = {
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`,
    x: `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodedTitle}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
    whatsapp: `https://wa.me/?text=${encodedTitle}%20${encodedUrl}`
  };

  const copyLink = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      setCopied(false);
    }
  };

  const itemClass =
    variant === 'compactSticky'
      ? 'inline-flex items-center justify-center gap-1 rounded-md border border-slate-200 bg-white px-2 py-1.5 text-[11px] font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50'
      : 'inline-flex items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 transition hover:border-slate-300 hover:bg-slate-50';

  const labels =
    variant === 'compactSticky'
      ? { linkedin: 'Li', x: 'X', facebook: 'Fb', whatsapp: 'Wa', copy: copied ? 'OK' : 'Copier' }
      : { linkedin: 'LinkedIn', x: 'X', facebook: 'Facebook', whatsapp: 'WhatsApp', copy: copied ? 'Lien copié' : 'Copier le lien' };

  const Icon = ({ children }) => (
    <svg viewBox="0 0 24 24" className="h-3.5 w-3.5 fill-current" aria-hidden="true">
      {children}
    </svg>
  );

  return (
    <div className={variant === 'compactSticky' ? 'rounded-lg border border-slate-200 bg-slate-50 p-2' : 'mt-5 rounded-lg border border-slate-200 bg-slate-50 px-3 py-3'}>
      <p className="mb-2 text-[11px] font-bold uppercase tracking-wider text-slate-500">
        {variant === 'compactSticky' ? 'Partager' : 'Partager cet article'}
      </p>
      <div className={variant === 'compactSticky' ? 'grid grid-cols-2 gap-2' : 'flex flex-wrap gap-2'}>
        <a href={links.linkedin} target="_blank" rel="noreferrer" className={itemClass} aria-label="Partager sur LinkedIn">
          <Icon><path d="M6.94 8.57H3.56V20h3.38V8.57ZM5.25 3A1.96 1.96 0 1 0 5.3 6.9 1.96 1.96 0 0 0 5.25 3ZM20 13.47c0-3.4-1.81-4.98-4.23-4.98-1.95 0-2.82 1.07-3.31 1.82v-1.56H9.08V20h3.38v-5.65c0-1.49.28-2.92 2.12-2.92 1.82 0 1.85 1.7 1.85 3.01V20H20v-6.53Z" /></Icon>
          {labels.linkedin}
        </a>

        <a href={links.x} target="_blank" rel="noreferrer" className={itemClass} aria-label="Partager sur X">
          <Icon><path d="M18.24 3H21l-6.03 6.9L22 21h-5.54l-4.34-5.66L7.17 21H4.4l6.45-7.37L2 3h5.68l3.92 5.18L18.24 3Zm-.97 16.3h1.53L6.84 4.6H5.2L17.27 19.3Z" /></Icon>
          {labels.x}
        </a>

        <a href={links.facebook} target="_blank" rel="noreferrer" className={itemClass} aria-label="Partager sur Facebook">
          <Icon><path d="M13.5 21v-8h2.7l.4-3h-3.1V8.1c0-.9.3-1.5 1.6-1.5h1.7V4c-.3 0-1.4-.1-2.6-.1-2.6 0-4.2 1.5-4.2 4.4V10H7v3h2.8v8h3.7Z" /></Icon>
          {labels.facebook}
        </a>

        <a href={links.whatsapp} target="_blank" rel="noreferrer" className={itemClass} aria-label="Partager sur WhatsApp">
          <Icon><path d="M20.5 3.5A11 11 0 0 0 3.8 17.3L2.5 21.5l4.3-1.2A11 11 0 1 0 20.5 3.5Zm-8.5 17a9.1 9.1 0 0 1-4.6-1.2l-.3-.2-2.5.7.7-2.4-.2-.4A9.1 9.1 0 1 1 12 20.5Zm5-6.8c-.3-.1-1.7-.8-2-.9-.2-.1-.4-.1-.6.1l-.9 1.1c-.2.2-.3.2-.6.1-1.6-.8-2.6-1.4-3.6-3.2-.2-.3 0-.4.1-.6l.4-.5.3-.4c.1-.2 0-.4 0-.5l-.9-2.2c-.2-.5-.5-.4-.6-.4h-.5c-.2 0-.5 0-.8.3-.3.4-1 1-1 2.5s1.1 3 1.2 3.2c.2.2 2.1 3.2 5.2 4.4.7.3 1.2.4 1.6.5.7.2 1.3.2 1.8.1.6-.1 1.7-.7 1.9-1.4.2-.7.2-1.3.2-1.4-.1 0-.3-.1-.6-.2Z" /></Icon>
          {labels.whatsapp}
        </a>

        <button
          type="button"
          onClick={copyLink}
          className={`${itemClass} ${variant === 'compactSticky' ? 'col-span-2' : ''}`}
          aria-label="Copier le lien de l'article"
        >
          <Icon><path d="M16 1H6a2 2 0 0 0-2 2v12h2V3h10V1Zm3 4H10a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h9a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2Zm0 16H10V7h9v14Z" /></Icon>
          {labels.copy}
        </button>
      </div>
    </div>
  );
};

export default ShareButtons;
